// WbDashboardPage — /weighbridge/dashboard (Phase C13).
//
// Composes the dashboard from existing endpoints — there is no dedicated
// /wb/dashboard summary route on the server. Layout:
//
//   Row 1 — 5 KPI tiles (parallel useQuery; each is a /wb/tickets list
//           with per_page=1 using pagination.total as the count, except
//           the active-alert tile which counts /wb/stock-alerts).
//   Row 2 — One <WbBridgeTile /> per active weighbridge (polled every 60s).
//   Row 3 — Recent completions table (2/3) + active stock alerts panel (1/3).
//
// All queries refresh every 30–60s via refetchInterval. No waterfalling —
// each useQuery fires independently so the tiles hydrate in parallel.

import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCheck,
  FileEdit,
  ListChecks,
  Scale,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Button } from '../../../components/ui/Button/Button';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { KpiCard } from '../../collections/dashboard/components/KpiCard';
import { useToastStore } from '../../../store/toastStore';
import { extractErrorMessage } from '../../../utils/errorMessage';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import { useWbWeighbridges } from '../hooks/useWbWeighbridges';
import {
  useDismissStockAlert,
  useWbStockAlerts,
  type StockAlertDto,
} from '../hooks/useWbStock';
import { useWbTickets, type TicketStatus } from '../hooks/useWbTickets';
import { WbBridgeTile } from '../components/WbBridgeTile';
import styles from './WbDashboardPage.module.css';

const KPI_STALE_MS = 30_000;
const FEED_REFETCH_MS = 60_000;

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

const KG_FORMATTER = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 1,
});

// -- KPI count hook ---------------------------------------------------------
//
// Tiny wrapper over /wb/tickets — only `pagination.total` is consumed.
// `per_page=1` keeps the response cheap; the rows are discarded.

interface TicketCountParams {
  status: TicketStatus;
  date_from?: string;
  date_to?: string;
}

// We only need the count — extract `pagination.total` from whichever shape
// the server returns. The real `/wb/tickets` controller emits
// `{ data: rows, total, page, per_page }`; older docs and test mocks shape
// the same payload as `{ tickets, pagination }`. Accept either.
interface TicketCountResult {
  total: number;
}

function useTicketCount(params: TicketCountParams) {
  const queryKey = queryKeys.wb.tickets({
    ...params,
    per_page: 1,
    _kpi: true,
  });
  return useQuery<TicketCountResult>({
    queryKey,
    queryFn: async () => {
      const sp = new URLSearchParams();
      sp.set('status', params.status);
      if (params.date_from !== undefined) sp.set('date_from', params.date_from);
      if (params.date_to !== undefined) sp.set('date_to', params.date_to);
      sp.set('per_page', '1');
      const res = await apiClient.get<unknown>(
        `${endpoints.wb.tickets}?${sp.toString()}`,
      );
      const body = (res.data ?? {}) as {
        pagination?: { total?: number };
        total?: number;
        tickets?: unknown[];
        data?: unknown[];
      };
      const total =
        body.pagination?.total ??
        body.total ??
        body.tickets?.length ??
        body.data?.length ??
        0;
      return { total };
    },
    staleTime: KPI_STALE_MS,
    refetchInterval: KPI_STALE_MS,
    refetchIntervalInBackground: false,
  });
}

// -- Recent completions feed ------------------------------------------------

function RecentCompletionsCard(): JSX.Element {
  const q = useWbTickets({ status: 'completed', per_page: 10, page: 1 });
  const rows = q.data?.tickets ?? [];

  return (
    <Card
      title="Recent completions"
      actions={
        <Link
          to="/weighbridge/tickets?status=completed"
          className={styles.viewAll}
        >
          View all tickets
        </Link>
      }
    >
      {q.isLoading ? (
        <div className={styles.loadingRow}>
          <Spinner label="Loading recent completions" />
        </div>
      ) : q.isError ? (
        <p className={styles.error}>{extractErrorMessage(q.error)}</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="No completed tickets today."
          description="Completed tickets appear here as they close."
        />
      ) : (
        <table className={styles.table} data-testid="recent-completions-table">
          <thead>
            <tr>
              <th>Ticket #</th>
              <th>Customer</th>
              <th className={styles.numeric}>Net weight</th>
              <th className={styles.numeric}>Total</th>
              <th>Completed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.enc_id}>
                <td>
                  <Link
                    to={`/weighbridge/tickets/${encodeURIComponent(t.enc_id)}`}
                    className={styles.cellLink}
                  >
                    {t.ticket_number ?? '—'}
                  </Link>
                </td>
                <td>{t.customer.name}</td>
                <td className={styles.numeric}>
                  {t.net_weight_kg !== null
                    ? `${KG_FORMATTER.format(t.net_weight_kg)} kg`
                    : '—'}
                </td>
                <td className={styles.numeric}>
                  {CURRENCY_FORMATTER.format(t.total)}
                </td>
                <td>{formatDateTime(t.completed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// -- Active stock alerts panel ---------------------------------------------

function StockAlertsCard(): JSX.Element {
  const pushToast = useToastStore((s) => s.push);
  const q = useWbStockAlerts(
    { status: 'active', per_page: 5 },
    { enablePolling: true },
  );
  const dismissMut = useDismissStockAlert();
  const alerts = q.data?.stock_alerts ?? [];

  async function handleDismiss(alert: StockAlertDto) {
    try {
      await dismissMut.mutateAsync(alert.enc_id);
      pushToast({ type: 'success', message: 'Alert dismissed.' });
    } catch (err) {
      pushToast({
        type: 'error',
        message: extractErrorMessage(err) ?? 'Failed to dismiss alert.',
      });
    }
  }

  return (
    <Card
      title="Active stock alerts"
      actions={
        <Link to="/weighbridge/stock" className={styles.viewAll}>
          View all
        </Link>
      }
    >
      {q.isLoading ? (
        <div className={styles.loadingRow}>
          <Spinner label="Loading alerts" />
        </div>
      ) : q.isError ? (
        <p className={styles.error}>{extractErrorMessage(q.error)}</p>
      ) : alerts.length === 0 ? (
        <p className={styles.alertsEmpty} data-testid="alerts-empty">
          No active stock alerts.
        </p>
      ) : (
        <ul className={styles.alertList} data-testid="alerts-list">
          {alerts.map((a) => (
            <li key={a.enc_id} className={styles.alertRow}>
              <div className={styles.alertHead}>
                <span className={styles.alertProduct}>{a.product.name}</span>
                <Badge
                  variant={a.threshold_type === 'low' ? 'danger' : 'warning'}
                >
                  {a.threshold_type === 'low' ? 'Low' : 'High'}
                </Badge>
              </div>
              <div className={styles.alertMeta}>
                <span>{a.yard.name}</span>
                <span aria-hidden>·</span>
                <span>{formatDateTime(a.triggered_at)}</span>
              </div>
              <div className={styles.alertActions}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDismiss(a)}
                  disabled={dismissMut.isPending}
                  data-testid={`dismiss-${a.enc_id}`}
                >
                  Dismiss
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// -- Page -------------------------------------------------------------------

export function WbDashboardPage(): JSX.Element {
  const today = todayIso();

  // KPI queries — each is independent so React Query fires them in parallel.
  const completionsKpi = useTicketCount({
    status: 'completed',
    date_from: today,
    date_to: today,
  });
  const openKpi = useTicketCount({ status: 'open' });
  const draftsKpi = useTicketCount({ status: 'draft' });

  // Stock alerts count — separate from the panel below because the panel
  // is capped at 5 rows; the KPI wants the unbounded count via pagination.total.
  const alertCountQuery = useWbStockAlerts(
    { status: 'active', per_page: 1 },
    { enablePolling: true },
  );

  const bridgesQuery = useWbWeighbridges(
    { is_active: true },
    { enablePolling: true },
  );
  const bridges = bridgesQuery.data?.weighbridges ?? [];

  function kpiValue(q: ReturnType<typeof useTicketCount>): number | null {
    if (q.isError) return null;
    return q.data?.total ?? null;
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Dashboard"
        subtitle="Live operations overview for the weighbridge module."
      />

      <div className={styles.kpiGrid}>
        <KpiCard
          label="Today's completions"
          value={kpiValue(completionsKpi)}
          icon={CheckCheck}
          loading={completionsKpi.isLoading}
          to="/weighbridge/tickets?status=completed"
        />
        <KpiCard
          label="Open tickets"
          value={kpiValue(openKpi)}
          icon={ListChecks}
          loading={openKpi.isLoading}
          to="/weighbridge/tickets/open"
        />
        <KpiCard
          label="Drafts"
          value={kpiValue(draftsKpi)}
          icon={FileEdit}
          loading={draftsKpi.isLoading}
          to="/weighbridge/tickets?status=draft"
        />
        <KpiCard
          label="Active stock alerts"
          value={
            alertCountQuery.isError
              ? null
              : (alertCountQuery.data?.pagination?.total ?? null)
          }
          icon={AlertTriangle}
          loading={alertCountQuery.isLoading}
          to="/weighbridge/stock"
        />
      </div>

      <section className={styles.bridgesSection}>
        <header className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <Scale size={16} aria-hidden /> Weighbridges
          </h2>
        </header>
        {bridgesQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading weighbridges" />
          </div>
        ) : bridges.length === 0 ? (
          <p className={styles.emptyMuted}>No active weighbridges.</p>
        ) : (
          <div
            className={styles.bridgeGrid}
            data-testid="dashboard-bridge-grid"
          >
            {bridges.map((wb) => (
              <WbBridgeTile key={wb.enc_id} weighbridge={wb} />
            ))}
          </div>
        )}
      </section>

      <div className={styles.feedGrid}>
        <RecentCompletionsCard />
        <StockAlertsCard />
      </div>

      <p className={styles.refreshNote}>
        Data refreshes every {Math.round(FEED_REFETCH_MS / 1000)}s in the
        background.
      </p>
    </div>
  );
}
